import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import PropTypes from 'prop-types'
import { bshSlice } from '~/store'

function plainTextFromHtml(html) {
  if (!html) {
    return ''
  }
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body.textContent?.replace(/\s+/g, ' ').trim() ?? ''
}

const IconChooseDialog = ({ open, targetParagraphId, onClose }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const editorParagraphs = useSelector(bshSlice.paragraphs)
  const groupParagraphs = useSelector(bshSlice.categoryParagraphs)
  const [pickingId, setPickingId] = useState(null)

  const rows = useMemo(() => {
    return groupParagraphs.filter(
      (p) => p.type === 'icon' && p.id !== targetParagraphId && Boolean(p.media)
    )
  }, [groupParagraphs, targetParagraphId])

  const pick = async (sourceParagraphId) => {
    const paragraph = editorParagraphs.find((p) => p.id === targetParagraphId)
    if (!paragraph) {
      return
    }
    setPickingId(sourceParagraphId)
    try {
      await dispatch(
        bshSlice.assignSideIconFromIconParagraph({
          data: { paragraph, sourceParagraphId },
        })
      ).unwrap()
      onClose()
    } catch {
      // Błąd obsłużony globalnie (toast).
    } finally {
      setPickingId(null)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 5 }}>
        {t('paragraph.icon-choose-dialog-title')}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {pickingId && <LinearProgress color="primary" />}
      <DialogContent dividers sx={rows.length > 0 ? { padding: 0 } : undefined}>
        {rows.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            {t('paragraph.icon-choose-empty')}
          </Typography>
        ) : (
          <Table size="small">
            <TableBody>
              {rows.map((p) => {
                const preview = plainTextFromHtml(p.text)
                return (
                  <TableRow
                    key={p.id}
                    hover
                    sx={{
                      cursor: pickingId === null && 'pointer',
                    }}
                    onClick={() => pick(p.id)}
                  >
                    <TableCell>
                      <Box
                        sx={{
                          width: 96,
                          height: 96,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                          overflow: 'hidden',
                        }}
                      >
                        <LazyLoadImage
                          alt=""
                          src={p.media}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'middle' }}>
                      <Typography color="text.secondary">
                        {preview || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  )
}

IconChooseDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  targetParagraphId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default IconChooseDialog
